import React from 'react';
import styled from 'styled-components';
import { useRouter } from 'next/router';

import { Page } from '@tager/web-components';
import { Nullable, ResourceType } from '@tager/web-core';

import { CustomApp_PageContext } from '@/typings/hocs';
import { getSharedThunkList } from '@/utils/thunks';
import { getCurrentAliasPage } from '@/modules/Blog/components/Top/components/helpers';
import {
  convertErrorToProps,
  convertSlugToPath,
  getCurrentPath,
} from '@/utils/common';
import {
  getBlogPostByAliasThunk,
  selectBlogPostByAliasResource,
} from '@/store/reducers/tager/blog';
import ErrorPage from '@/pages/_error';
import Layout from '@/components/Layout';
import { PostFull } from '@/typings/model';
import NotFoundPage from '@/pages/404';
import PostView from '@/modules/Post/PostView';
import Post from '@/modules/Post';

type Props =
  | { pageType: 'Post'; post: ResourceType<Nullable<PostFull>> }
  | { pageType: 'Error'; error: Error }
  | { pageType: 'NOT_FOUND' };

const PostPage = (props: Props) => {
  const router = useRouter();
  const currentPath = getCurrentPath(router);
  const canonicalUrl = process.env.NEXT_PUBLIC_ORIGIN + currentPath;
  if (props.pageType === 'Error') {
    return <ErrorPage {...convertErrorToProps(props.error)} />;
  }

  if (props.pageType === 'NOT_FOUND') {
    return <NotFoundPage />;
  }

  const { post } = props;

  return (
    <Page
      title={post?.data?.pageTitle}
      description={post?.data?.pageDescription}
      openGraphImage={
        post?.data?.openGraphImage?.url ??
        post?.data?.image?.openGraph?.url ??
        ''
      }
      canonicalUrl={canonicalUrl}
    >
      <Layout>
        <Post post={post.data} />
      </Layout>
    </Page>
  );
};

export default PostPage;

PostPage.getInitialProps = async (
  context: CustomApp_PageContext
): Promise<Props> => {
  const { store, query, res } = context;

  const path = getCurrentAliasPage(convertSlugToPath(query.post));

  try {
    const requests = Promise.all([
      ...getSharedThunkList(store.dispatch),
      store.dispatch(getBlogPostByAliasThunk(path)),
    ]);

    await requests;

    const foundPost = selectBlogPostByAliasResource(store.getState(), path);

    if (!foundPost?.data?.id) {
      if (res) {
        res.statusCode = 404;
      }
      return { pageType: 'NOT_FOUND' };
    }

    return { pageType: 'Post', post: foundPost };
  } catch (error) {
    return { pageType: 'Error', error };
  }
};
